import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useField } from 'formik';
import React from 'react';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

interface DaySelectorProps {
  value: number[];
  onChange: (days: number[]) => void;
  label?: string;
  /**
   * When provided, only these days are selectable, others are disabled. Uses
   * JS day-of-week indices: 0 = Sun, 1 = Mon, …, 6 = Sat.
   */
  allowedDays?: number[];
}

interface FormDaySelectorProps {
  name: string;
  label?: string;
}

const DAYS = [
  { id: 0, name: 'Sun' },
  { id: 1, name: 'Mon' },
  { id: 2, name: 'Tue' },
  { id: 3, name: 'Wed' },
  { id: 4, name: 'Thu' },
  { id: 5, name: 'Fri' },
  { id: 6, name: 'Sat' },
];

export const DaySelector: React.FC<DaySelectorProps> = ({
  value,
  onChange,
  label,
  allowedDays,
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDays: number[]
  ) => {
    onChange(newDays);
  };

  const hasRestriction = allowedDays && allowedDays.length > 0;

  return (
    <Stack spacing={1}>
      {label && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
      <ToggleButtonGroup
        value={value}
        onChange={handleChange}
        aria-label="days of week"
        size="small"
        fullWidth
      >
        {DAYS.map((day) => (
          <ToggleButton
            key={day.id}
            value={day.id}
            aria-label={day.name}
            disabled={hasRestriction && !allowedDays.includes(day.id)}
            sx={{
              '&.MuiToggleButton-root': {
                textTransform: 'none',
                px: 1,
              },
            }}
          >
            {day.name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
};

export const FormDaySelector: React.FC<FormDaySelectorProps> = ({
  name,
  label,
}) => {
  const [field, meta, helpers] = useField<number[]>(name);

  return (
    <Stack spacing={1}>
      <DaySelector
        value={field.value}
        onChange={(days) => helpers.setValue(days)}
        label={label}
      />
      {meta.touched && meta.error && (
        <Typography color="error" variant="caption">
          {meta.error}
        </Typography>
      )}
    </Stack>
  );
};
